import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useOrganizationEntitlements } from '@/hooks/useFeatureLimits';
import { usePendingOnboardingAssets } from '@/hooks/usePendingOnboardingAssets';
import { Loader2 } from 'lucide-react';

interface FeatureRouteProps {
  featureKey: string | null; // null means always accessible
  children: React.ReactNode;
  fallback?: string; // path to redirect to if feature is not enabled
  /**
   * Second feature that ALSO grants access. For routes that host more than one
   * feature — `/home/integrations` carries both the integrations grid and the
   * Social accounts tab (`social_media_posts`), so an org entitled to
   * only one of them must still be able to open the page.
   */
  alsoAllowFeatureKey?: string;
  /**
   * Feature that is ALSO REQUIRED (logical AND with `featureKey`). The mirror
   * image of `alsoAllowFeatureKey`: use it when a route needs two entitlements
   * at once rather than either of two.
   *
   * Shipped for the Facebook automations routes (`/home/automation/facebook*`),
   * which need BOTH `facebook_automation` (the automation feature) AND
   * `facebook_posts` (the Facebook channel — the mirror of
   * `sub_features.integrations.facebook`, see EntitlementService
   * `_syncLegacyMenuFeatureKeys`). Facebook DM/comment automations cannot do
   * anything without a connected Facebook Page, so an org denied the channel
   * must not reach the builder by deep link.
   *
   * Deliberately NOT bypassable by `allowOnboardingAccess`. That bypass exists
   * so an org with a pending onboarding WhatsApp flow can open
   * `/home/automation` before its plan grants `automations`; it says nothing
   * about whether the org may use a social channel it was never granted, and
   * letting it waive this gate would reopen the exact hole this prop closes.
   */
  requireFeatureKey?: string;
}

/**
 * FeatureRoute component - Protects routes based on feature enablement
 * Redirects to fallback path if feature is not enabled
 * 
 * @example
 * <Route path="contacts" element={
 *   <FeatureRoute featureKey="contacts" fallback="/home">
 *     <Contacts />
 *   </FeatureRoute>
 * } />
 */
export const FeatureRoute: React.FC<FeatureRouteProps> = ({
  featureKey,
  children,
  fallback = '/home',
  alsoAllowFeatureKey,
  requireFeatureKey,
}) => {
  const location = useLocation();
  const { data: entitlements, isLoading } = useOrganizationEntitlements();
  const { data: pendingAssets, isLoading: pendingAssetsLoading } = usePendingOnboardingAssets();
  const enabledFeatures = entitlements?.entitlements?.features || {};

  // Always allow if no feature key (e.g., Overview, Settings). A route that
  // declares only `requireFeatureKey` still has to be checked, so this
  // shortcut is limited to the "no gates at all" case.
  if (!featureKey && !requireFeatureKey) {
    return <>{children}</>;
  }

  // Show loading state while checking entitlements
  if (isLoading || pendingAssetsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // Map child feature keys to their parent plan feature
  const childToParent: Record<string, string> = {
    // Commerce sub-features
    orders: 'commerce',
    products: 'commerce',
    abonded_carts: 'commerce',
    payments: 'commerce',
    store_reports: 'commerce',
    // Appointments sub-features
    scheduling: 'appointments',
    time_slots: 'appointments',
    meeting_links: 'appointments',
    whatsapp_reminders: 'appointments',
  };

  // Check if feature is enabled (directly or via parent)
  const isFeatureOn = (key: string) =>
    enabledFeatures[key] === true ||
    (childToParent[key] && enabledFeatures[childToParent[key]] === true);

  const isEnabled = (!featureKey || isFeatureOn(featureKey)) ||
    (!!alsoAllowFeatureKey && isFeatureOn(alsoAllowFeatureKey));

  // AND-gate. Fail-closed by construction: a missing / false / still-unknown
  // entitlement leaves `enabledFeatures[key]` !== true, so a failed
  // subscription fetch denies rather than grants.
  const meetsRequiredFeature = !requireFeatureKey || isFeatureOn(requireFeatureKey);

  const hasPendingTemplates = Array.isArray(pendingAssets?.pending_templates) && pendingAssets.pending_templates.length > 0;
  const hasPendingFlow = !!pendingAssets?.pending_flow;
  const hasActivatedOnboardingDrafts = !!pendingAssets?.activated_business_account_id;
  const isTemplatesRoute = location.pathname.startsWith('/home/templates');
  const isAutomationRoute = location.pathname.startsWith('/home/automation');
  const isBusinessAccountsRoute = location.pathname.startsWith('/home/business-accounts');
  const allowOnboardingAccess =
    (isTemplatesRoute && (hasPendingTemplates || hasActivatedOnboardingDrafts)) ||
    (isAutomationRoute && (hasPendingFlow || hasActivatedOnboardingDrafts)) ||
    (isBusinessAccountsRoute && (hasPendingTemplates || hasPendingFlow || hasActivatedOnboardingDrafts));

  if ((!isEnabled && !allowOnboardingAccess) || !meetsRequiredFeature) {
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
};
